import {
  ACTIVATION_CODE_LENGTH,
  ACTIVATION_CODE_FORMATTED_LENGTH,
} from '@metabolic/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import {
  useVerifyActivationCode,
  useRegisterPasskey,
} from '../hooks/useActivation';
import {
  registerPasskey,
  type SerializedPublicKeyCredentialCreationOptions,
} from '../lib/webauthn';

/**
 * ActivationPage Component
 *
 * Handles new user activation and passkey registration flow.
 *
 * Flow:
 * 1. User enters email and activation code (provided out-of-band by admin)
 * 2. Verify activation code via backend API
 * 3. Guide user through passkey registration using WebAuthn
 * 4. Sign in with newly created passkey
 * 5. Redirect to dashboard
 */
export function ActivationPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'code' | 'passkey'>('code');

  // Store verification data for passkey registration
  const [verificationData, setVerificationData] = useState<{
    cognitoUsername: string;
    registrationOptions: SerializedPublicKeyCredentialCreationOptions;
    challenge: string;
  } | null>(null);

  // Mutations
  const verifyCode = useVerifyActivationCode();
  const registerPasskeyMutation = useRegisterPasskey();

  async function handleActivationSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Verify activation code with backend
      const result = await verifyCode.mutateAsync({
        email,
        activationCode,
      });

      if (result.valid) {
        // Store verification data for passkey registration
        setVerificationData({
          cognitoUsername: result.cognitoUsername,
          registrationOptions: result.registrationOptions,
          challenge: result.registrationOptions.challenge,
        });

        // Move to passkey setup step
        setStep('passkey');
      } else {
        setError(
          'Invalid activation code. Please check your code and try again.'
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Invalid activation code. Please check your code and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasskeySetup() {
    setError(null);
    setIsLoading(true);

    if (!verificationData) {
      setError('Verification data not found. Please start over.');
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Create passkey using WebAuthn API
      const credential = await registerPasskey(
        verificationData.registrationOptions
      );

      // Step 2: Register passkey with backend
      // Backend returns tokens which we'll use to establish Amplify session
      await registerPasskeyMutation.mutateAsync({
        email,
        activationCode,
        cognitoUsername: verificationData.cognitoUsername,
        registrationResponse: credential,
        expectedChallenge: verificationData.challenge,
      });

      // Step 3: Sign in with Amplify to establish proper session
      // This will use the passkey we just registered
      const result = await signIn();

      if (!result.isSignedIn) {
        setError(result.error || 'Failed to sign in after registration');
        return;
      }

      // Step 4: Redirect to dashboard
      // User is now authenticated via Amplify-managed tokens
      navigate('/dashboard');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to set up passkey. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  if (step === 'passkey') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-cream">
        <div className="card max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🔐</div>
            <h2 className="text-2xl font-semibold mb-2 text-text-dark">
              Set Up Your Passkey
            </h2>
            <p className="text-text-secondary">
              Create a passkey using your device's biometric authentication
              (Face ID, Touch ID, or Windows Hello)
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-beige">
              <h3 className="font-semibold mb-2 text-text-dark">
                What is a passkey?
              </h3>
              <ul className="text-sm space-y-2 text-text-secondary">
                <li>✓ More secure than passwords</li>
                <li>✓ Uses your device's biometric authentication</li>
                <li>✓ Can't be phished or stolen</li>
                <li>✓ Faster than typing a password</li>
              </ul>
            </div>

            <button
              onClick={handlePasskeySetup}
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Setting up passkey...' : 'Create Passkey'}
            </button>

            <button
              onClick={() => {
                setError(null);
                setStep('code');
              }}
              disabled={isLoading}
              className="w-full px-6 py-3 rounded-full font-semibold text-text-dark bg-beige hover:bg-opacity-80 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-cream">
      <div className="card max-w-md w-full">
        <h2 className="text-2xl font-semibold mb-6 text-text-dark">
          Activate Your Account
        </h2>

        <p className="mb-6 text-text-secondary">
          Enter your email address and the activation code provided by your
          administrator.
        </p>

        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleActivationSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-2 text-text-dark"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label
              htmlFor="code"
              className="block text-sm font-medium mb-2 text-text-dark"
            >
              Activation Code
            </label>
            <input
              id="code"
              type="text"
              value={activationCode}
              onChange={(e) => {
                // Auto-format activation code with dashes (XXXX-XXXX-XXXX-XXXX)
                let value = e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, '');

                // Limit to activation code length (16 characters)
                if (value.length > ACTIVATION_CODE_LENGTH) {
                  value = value.slice(0, ACTIVATION_CODE_LENGTH);
                }

                // Add dashes after every 4 characters
                const formatted = value.match(/.{1,4}/g)?.join('-') || value;
                setActivationCode(formatted);
              }}
              required
              maxLength={ACTIVATION_CODE_FORMATTED_LENGTH}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary font-mono uppercase tracking-wider"
              placeholder="XXXX-XXXX-XXXX-XXXX"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full"
          >
            {isLoading ? 'Verifying...' : 'Continue'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-text-secondary">
            Already activated?{' '}
            <button
              onClick={() => navigate('/signin')}
              className="text-primary font-semibold hover:underline"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
